import { computed, signal } from "@preact/signals-react";
import apiService from "./api-service";
import { batch } from "@preact/signals-core";

const commonTemplate = (initialState) => {
  return {
    data: signal(initialState),
    isLoading: signal(false),
  };
};

const createCustomerModel = (customerNumber) => {
  return {
    customerNumber: customerNumber,
    profile: {
      ...commonTemplate({}),
      load() {
        this.isLoading.value = true;
        return apiService
          .get(`/customers/${customerNumber}/profile`)
          .then(({ data }) => {
            this.data.value = data;
          })
          .catch(() => {})
          .finally(() => {
            this.isLoading.value = false;
          });
      },
    },
    installations: {
      ...commonTemplate([]),
      cars: signal([]),
      load() {
        this.isLoading.value = true;
        return apiService
          .get(`/customers/${customerNumber}/installations`)
          .then(({ data }) => {
            this.data.value = data;
          })
          .catch(() => {})
          .finally(() => {
            this.isLoading.value = false;
          });
      },
      add(values) {
        this.isLoading.value = true;
        const promise = apiService.post(`/customers/${customerNumber}/installations`, values);
        promise
          .then(({ data }) => {
            this.data.value = [...this.data.value, data];
          })
          .catch(() => {})
          .finally(() => {
            this.isLoading.value = false;
          });
        return promise;
      },
    },
    cars: {
      ...commonTemplate([]),
      load() {
        this.isLoading.value = true;
        return apiService
          .get(`/customers/${customerNumber}/cars`)
          .then(({ data }) => {
            this.data.value = data;
          })
          .catch(() => {})
          .finally(() => {
            this.isLoading.value = false;
          });
      },
      add(values) {
        this.isLoading.value = true;
        const promise = apiService.post(`/customers/${customerNumber}/cars`, values);
        promise
          .then(({ data }) => {
            batch(() => {
              this.data.value = [...this.data.value, data];
            });
          })
          .catch(() => {})
          .finally(() => (this.isLoading.value = false));
        return promise;
      },
    },
    devices: {
      ...commonTemplate([]),
      load() {
        this.isLoading.value = true;
        return apiService
          .get(`/customers/${customerNumber}/devices`)
          .then(({ data }) => {
            this.data.value = data;
          })
          .catch(() => {})
          .finally(() => {
            this.isLoading.value = false;
          });
      },
      add(values) {
        this.isLoading.value = true;
        const promise = apiService.post(`/customers/${customerNumber}/devices`, values);
        promise
          .then(({ data }) => {
            batch(() => {
              this.data.value = [...this.data.value, data];
            });
          })
          .catch(() => {})
          .finally(() => (this.isLoading.value = false));
        return promise;
      },
    },
  };
};

export const store = {
  customers: {
    dict: signal({}),
    idDict: {},
    getCustomerList() {
      if (this.computedObjects.customerList === null) {
        this.computedObjects.customerList = computed(() =>
          Object.keys(this.dict.value).map((key) => this.dict.value[key].profile.data.value),
        );
      }
      return this.computedObjects.customerList;
    },
    computedObjects: {
      customerList: null,
    },
    isLoading: signal(false),
    load() {
      if (this.isLoading.value) {
        return;
      }
      this.isLoading.value = true;
      apiService
        .get("/customers")
        .then(({ data }) => {
          const newDict = { ...this.dict.value };
          data.forEach((item) => {
            if (!(item.customerNumber in newDict)) {
              newDict[item.customerNumber] = createCustomerModel(item.customerNumber);
            }
            newDict[item.customerNumber].profile.data.value = item;
            this.idDict[item.id] = newDict[item.customerNumber];
          });
          this.dict.value = newDict;
        })
        .catch((error) => console.error(error.message))
        .finally(() => (this.isLoading.value = false));
    },
    get(customerNumber) {
      return this.dict.value[customerNumber] ?? null;
    },
    getById(id) {
      return this.idDict[id];
    },
    connect(customerNumber, zipCode, birthDate) {
      this.isLoading.value = true;
      const response = apiService.post('/customers', {
        customerNumber: parseInt(customerNumber),
        zipCode,
        ownerBirthdate: birthDate
      });

      response.then(() => {
        this.load();
      })
      .catch((error) => console.error(error.message))
      .finally(() => (this.isLoading.value = false));
    }
  },
  technician: {
    ...commonTemplate(null),
    login(values) {
      this.isLoading.value = true;
      return apiService
        .post("/login", values)
        .then(({ data }) => {
          this.data.value = data;
          this.recentInstallations.data.value = data.recentInstallations;
          localStorage.setItem("technician", data.name);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => (this.isLoading.value = false));
    },
    logout() {
      this.isLoading.value = true;
      return apiService
        .get("/logout")
        .then(() => {
          this.data.value = null;
          localStorage.removeItem("technician");
        })
        .catch(() => {})
        .finally(() => (this.isLoading.value = false));
    },
    load() {
      this.isLoading.value = true;
      return apiService
        .get("/dashboard")
        .then(({ data }) => {
          this.data.value = data;
          this.recentInstallations.data.value = data.recentInstallations;
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => (this.isLoading.value = false));
    },
    recentInstallations: {
      ...commonTemplate([]),
      load() {
        this.isLoading.value = true;
        return apiService
        .get(`/installations/recent`)
        .then(({ data }) => (this.data.value = data))
        .catch((error) => {
          console.error(error);
        })
        .finally(() => (this.isLoading.value = false));
      },
      getShifts() {
        const deviceTypeDict = {
          "4": "Hale SEI-03",
          "9": "Hale MCT07"
        };
        const shifts = [];
        this.data.value.forEach((installation) => {
          installation.shifts.forEach((shift) => {
            shift.deviceType = deviceTypeDict[installation.deviceType];
            shift.deviceManufacturerNumber = installation.deviceManufacturerNumber;
            shift.id = shift.uuid;
            shift.customerNumber = installation.customerNumber;
            shift.customerBusinessName = installation.customerBusinessName;
            shift.installationUuid = installation.uuid;
            shifts.push(shift);
          });
        });

        return shifts;
      }
    }
  },
  currentCustomer: signal(createCustomerModel(0)),
  installations: {
    ...commonTemplate({ installations: [], cars: [] }),
    load() {
      this.isLoading.value = true;
      return apiService
        .get(`/installations`)
        .then(({ data }) => (this.data.value = data))
        .catch((error) => {
          console.error(error);
        })
        .finally(() => (this.isLoading.value = false));
    },
  },
  installationAssistant: {
    customerNumber: signal(""),
    customerIsKnown: signal(false),
    vehicleVIN: signal(""),
    vehicleMileage: signal(""),
    vehicleExists: signal(false),
    licenseNumber: signal(""),
    carNotRegistered: signal(false),
    cars: signal([]),
    businessName: signal(""),
    deviceData: signal({
      serialNumber: "",
      deviceType: "",
      hasPrinter: false,
      hasGPS: false
    }),
    reset() {
      this.customerNumber.value = "";
      this.customerIsKnown.value = false;
      this.vehicleVIN.value = "";
      this.vehicleMileage.value = "";
      this.vehicleExists.value = false;
      this.licenseNumber.value = "";
      this.carNotRegistered.value = false,
      this.cars.value = [];
      this.businessName.value = "";
      this.deviceData.value = {
        serialNumber: "",
        deviceType: "",
        hasPrinter: false,
        hasGPS: false
      };
    }
  }
};
